import React, { useState, useEffect } from 'react';
import InvoiceImageService from '../services/InvoiceImageService';

interface ImagePreviewProps {
  imagePath: string;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ imagePath }) => {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [imageService] = useState(() => new InvoiceImageService());

  useEffect(() => {
    const loadImage = async () => {
      try {
        const url = await imageService.getImageUrl(imagePath);
        setImageUrl(url);
      } catch (error) {
        console.error('Error loading image:', error);
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [imagePath, imageService]);

  const handleDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('Download button clicked for:', imagePath);
    
    try {
      // Fetch the file as a blob from our backend
      const downloadUrl = `${imageService.apiBaseUrl}/invoices/download/${imagePath}`;
      console.log('Downloading from:', downloadUrl);
      
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = imagePath.split('/').pop() || 'invoice-image.jpg';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(url);
      console.log('Download completed');
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center"
           style={{ height: '150px', backgroundColor: '#f8f9fa' }}>
        <div className="spinner-border spinner-border-sm text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!imageUrl) {
    return (
      <div className="d-flex align-items-center justify-content-center"
           style={{ height: '150px', backgroundColor: '#f8f9fa' }}>
        <span className="text-muted">Image not found</span>
      </div>
    );
  }

  return (
    <div className="position-relative">
      <a href={imageUrl} target="_blank" rel="noopener noreferrer">
        <img
          src={imageUrl}
          alt="Invoice attachment"
          className="img-fluid rounded"
          style={{ maxHeight: '150px', width: '100%', objectFit: 'cover' }}
        />
      </a>
      <button
        type="button"
        onClick={handleDownload}
        className="btn btn-sm btn-light position-absolute"
        style={{ 
          top: '5px', 
          right: '5px', 
          padding: '0.25rem 0.5rem',
          opacity: 0.8,
          zIndex: 10
        }}
        title="Download image"
      >
        <i className="fas fa-download"></i>
      </button>
    </div>
  );
};

export default ImagePreview;